import React, { createContext, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import useQuery from "../../custom-hooks/useQuery/useQuery";
import { CompanyLegalEntityDTO } from "../../models/company-accounts/company-legal-entities";
import { Panel } from "../../reusable-components/MultipleTabs/interfaces";
import MultipleTabs from "../../reusable-components/MultipleTabs/MultipleTabs";
import { LegalEntityQuickfilter, useLegalEntityQuickFilter } from "../Quickfilters/LegalEntityQuickfilter";
import { LegalEntityOptionValue } from "../Quickfilters/LegalEntityQuickfilter/interfaces";
import TnAOverviewsWrapper from "./TnAOverviews/TnAOverviewsWrapper";
import { Topbar, Title } from "./styles";
import TnACalendar from "./TnACalendar/TnACalendar";
import TnAVacationTracking from "./TnAVacationTracking/TnAVacationTracking";
import TnAWorkedTime from "./TnAWorkedTime/TnAWorkedTime";
import TnAExtraHours from "./TnAExtraHours/TnAExtraHours";
import TnARules from "./TnARules/TnARules";
import TnAPolicies from "./TnAPolicies/TnAPolicies";
import TnATimeSheetConfirmation from "./TnATimeSheetConfirmation/TnATimeSheetConfirmation";
import TnABillableTimeOverviews from "./TnABillableTimeOverviews/TnABillableTimeOverviews";
import { EmployeeGroupOptionValue } from "../Quickfilters/EmployeeGroupQuickfilter/interfaces";
import { EmployeeGroupQuickfilter, useEmployeeGroupQuickFilter } from "../Quickfilters/EmployeeGroupQuickfilter";
import { CurrentEmployeesQuickfilter } from "../Quickfilters/CurrentEmployeesQuickfilter/CurrentEmployeesQuickfilter";
import { useCurrentEmployeesQuickFilter } from "../Quickfilters/CurrentEmployeesQuickfilter/useCurrentEmployeesQuickFilter";
import { OnlyCurrentEmployeesOptionValue } from "../Quickfilters/CurrentEmployeesQuickfilter/interfaces";
import FilterByYearAndMonth from "../FilterByYearAndMonth/FilterByYearAndMonth";
import useChangeYearAndMonth from "../FilterByYearAndMonth/useChangeYearAndMonth/useChangeYearAndMonth";
import { useAppSelector } from "../../custom-hooks/redux/redux";
import useAuthorization, { isTabAuthorized } from "../../custom-hooks/useAuthorization/useAuthorization";
import { MyTeamPresenceAndLeavesLinks } from "../../widgetLink";
import { NotAuthorizedPage } from "../../reusable-components/NotAuthorizedPage/NotAuthorizedPage";
import { AUTHORIZATIONS } from "../../utils/constants/authorization-constants";
import {
  faCalendar,
  faCalendarCheck,
  faCheckCircle,
  faClock,
  faDashboard,
  faHotel,
  faMoneyBill,
  faStopwatch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";
import FilterByYear from "../FilterByYear/FilterByYear";
import AllEmployeeLeaves from "./AllEmployeeLeaves/AllEmployeeLeaves";
import TnAEmployeesApprovers from "./TnAEmployeesApprovers/TnAEmployeesApprovers";
interface Filter {
  legalEntity: {
    legalEntityOption: LegalEntityOptionValue | null;
    selectedLegalEntity: CompanyLegalEntityDTO | null;
    legalEntityQuickFilter: JSX.Element | null;
  };
  employeeGroup: {
    employeeGroupOption: EmployeeGroupOptionValue | null;
    employeeGroupQuickFilter: JSX.Element | null;
  };
  currentEmployees: {
    onlyCurrentEmployeesOptionValue: OnlyCurrentEmployeesOptionValue | null;
    onlyCurrentEmployeesQuickFilter: JSX.Element | null;
  };
  searchingDate: {
    year: number;
    month: number;
    yearMonthQuickFilter: JSX.Element | null;
    yearQuickFilter: JSX.Element | null;
  };
}

export const MyTnAContext = createContext<Filter>({
  legalEntity: {
    legalEntityOption: null,
    selectedLegalEntity: null,
    legalEntityQuickFilter: null,
  },
  employeeGroup: {
    employeeGroupOption: null,
    employeeGroupQuickFilter: null,
  },
  currentEmployees: {
    onlyCurrentEmployeesOptionValue: null,
    onlyCurrentEmployeesQuickFilter: null,
  },
  searchingDate: {
    year: 0,
    month: 0,
    yearMonthQuickFilter: null,
    yearQuickFilter: null,
  },
});

export enum TEAM_PRESENCE_AND_LEAVES {
  TEAM_PRESENCE_AND_LEAVES_OVERVIEW = "dashboard",
  TEAM_PRESENCE_AND_LEAVES_CALENDAR = "calendar",
  VACATION_TRACKING = "vacation-tracking",
  ALL_EMPLOYEE_LEAVES = "all-employee-leaves",
  WORKED_TIME = "worked-time",
  EXTRA_HOURS = "extra-hours",
  BILLABLE_TIME = "billable-time",
  RULES = "rules",
  APPROVERS = "my-team-approvers",
  POLICIES = "policies",
  TIME_SHEET_CONFIRMATION = "timesheet-confirmation",
}

const MyTeamPresenceAndLeaves = () => {
  const { t } = useTranslation();
  const tRP = "components.widgets.my_team_presence_and_leaves"; // tRP - translation root path

  const {
    legalEntitiesQuickFilter,
    setLegalEntitiesQuickFilter,
    selectedLegalEntity,
    legalEntityQFIsLoading,
    allLegalEntities,
  } = useLegalEntityQuickFilter(true);

  const { employeeGroupQuickFilter, setEmployeeGroupQuickFilter } = useEmployeeGroupQuickFilter();

  const { onlyCurrentEmployeesQuickFilter, setOnlyCurrentEmployeesQuickFilter } = useCurrentEmployeesQuickFilter();

  const { year, month, handleChangeYearAndMonth, handleChangeYear } = useChangeYearAndMonth();

  const { currentUserAuthorizations, currentUserData } = useAppSelector((state) => state.currentUserReducer);

  const checkPanelAccess = useCallback((showPanel: boolean, panel: Panel) => (showPanel ? [panel] : []), []);

  const hasAccess = useAuthorization([
    AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves,
    AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData,
  ]);

  const allowOverviews =
    isTabAuthorized(MyTeamPresenceAndLeavesLinks.MyTeamPresenceAndLeaves, currentUserAuthorizations, currentUserData) ||
    hasAccess;

  let query = useQuery();

  const panels: Panel[] = useMemo(
    () => [
      ...checkPanelAccess(allowOverviews, {
        tabLabel: t(`${tRP}.tables.dashboard`),
        tabIcon: <FontAwesomeIcon icon={faDashboard} />,
        tableTitle: t(`${tRP}.tables.dashboard`),
        tabPanelComponent: <TnAOverviewsWrapper />,
        routePath: TEAM_PRESENCE_AND_LEAVES.TEAM_PRESENCE_AND_LEAVES_OVERVIEW,
      }),
      ...checkPanelAccess(allowOverviews, {
        tabLabel: t(`${tRP}.links.calendar`),
        tabIcon: <FontAwesomeIcon icon={faCalendar} />,
        tableTitle: t(`${tRP}.tables.calendar`),
        tabPanelComponent: <TnACalendar />,
        routePath: TEAM_PRESENCE_AND_LEAVES.TEAM_PRESENCE_AND_LEAVES_CALENDAR,
      }),
      ...checkPanelAccess(allowOverviews, {
        tabLabel: t(`${tRP}.links.vacation_tracking`),
        tabIcon: <FontAwesomeIcon icon={faHotel} />,
        tableTitle: t(`${tRP}.tables.vacation_tracking`),
        tabPanelComponent: <TnAVacationTracking />,
        routePath: TEAM_PRESENCE_AND_LEAVES.VACATION_TRACKING,
      }),
      ...checkPanelAccess(allowOverviews, {
        tabLabel: t(`${tRP}.links.all_employee_leaves`),
        tabIcon: <FontAwesomeIcon icon={faHotel} />,
        tableTitle: t(`${tRP}.tables.all_employee_leaves`),
        tabPanelComponent: <AllEmployeeLeaves />,
        routePath: TEAM_PRESENCE_AND_LEAVES.ALL_EMPLOYEE_LEAVES,
      }),
      ...checkPanelAccess(allowOverviews, {
        tabLabel: t(`${tRP}.links.worked_time`),
        tabIcon: <FontAwesomeIcon icon={faClock} />,
        tableTitle: t(`${tRP}.tables.worked_time`),
        tabPanelComponent: <TnAWorkedTime />,
        routePath: TEAM_PRESENCE_AND_LEAVES.WORKED_TIME,
      }),
      ...checkPanelAccess(allowOverviews, {
        tabLabel: t(`${tRP}.links.extra_hours`),
        tabIcon: <FontAwesomeIcon icon={faStopwatch} />,
        tableTitle: t(`${tRP}.tables.extra_hours`),
        tabPanelComponent: <TnAExtraHours />,
        routePath: TEAM_PRESENCE_AND_LEAVES.EXTRA_HOURS,
      }),
      ...checkPanelAccess(allowOverviews, {
        tabLabel: t(`${tRP}.links.billable_time`),
        tabIcon: <FontAwesomeIcon icon={faMoneyBill} />,
        tableTitle: t(`${tRP}.tables.billable_time`),
        tabPanelComponent: <TnABillableTimeOverviews />,
        routePath: TEAM_PRESENCE_AND_LEAVES.BILLABLE_TIME,
      }),
      ...checkPanelAccess(allowOverviews, {
        tabLabel: t(`${tRP}.links.rules`),
        tabIcon: <FontAwesomeIcon icon={faCalendarCheck} />,
        tableTitle: t(`${tRP}.tables.rules`),
        tabPanelComponent: <TnARules />,
        routePath: TEAM_PRESENCE_AND_LEAVES.RULES,
      }),
      // {
      //   tabLabel: t(`${tRP}.links.policies`),
      //   tableTitle: t(`${tRP}.tables.policies`),
      //   tabPanelComponent: <TnAPolicies />,
      //   routePath: TEAM_PRESENCE_AND_LEAVES.POLICIES,
      // }),
      ...checkPanelAccess(allowOverviews, {
        tabLabel: t(`${tRP}.links.timesheet_confirmation`),
        tabIcon: <FontAwesomeIcon icon={faCheckCircle} />,
        tableTitle: t(`${tRP}.tables.timesheet_confirmation`),
        tabPanelComponent: <TnATimeSheetConfirmation />,
        routePath: TEAM_PRESENCE_AND_LEAVES.TIME_SHEET_CONFIRMATION,
      }),
      ...checkPanelAccess(allowOverviews, {
        tabLabel: t(`${tRP}.links.employee_approvers`),
        tabIcon: <FontAwesomeIcon icon={faCheckCircle} />,
        tableTitle: "Employee approvers",
        tabPanelComponent: <TnAEmployeesApprovers />,
        routePath: TEAM_PRESENCE_AND_LEAVES.APPROVERS,
      }),
    ],
    [t]
  );

  const selectedTab = useMemo(() => {
    const foundIndex = panels.findIndex((panel) => panel.routePath === query.get("tab"));
    return foundIndex >= 0 ? foundIndex : 0;
  }, [query, panels]);

  return (
    <>
      {panels.length ? (
        <MyTnAContext.Provider
          value={{
            legalEntity: {
              legalEntityOption: legalEntitiesQuickFilter.legalEntities,
              selectedLegalEntity: selectedLegalEntity,
              legalEntityQuickFilter: (
                <LegalEntityQuickfilter
                  quickFilters={legalEntitiesQuickFilter}
                  handleChangeQuickFilters={setLegalEntitiesQuickFilter}
                  allLegalEntitiesOption
                  allLegalEntities={allLegalEntities}
                />
              ),
            },

            employeeGroup: {
              employeeGroupOption: employeeGroupQuickFilter.employeeGroups,
              employeeGroupQuickFilter: (
                <EmployeeGroupQuickfilter
                  quickFilters={employeeGroupQuickFilter}
                  handleChangeQuickFilters={setEmployeeGroupQuickFilter}
                  allEmployeeGroupsOption
                />
              ),
            },

            currentEmployees: {
              onlyCurrentEmployeesOptionValue: onlyCurrentEmployeesQuickFilter.onlyWithCurrentEmployees,
              onlyCurrentEmployeesQuickFilter: (
                <CurrentEmployeesQuickfilter
                  quickFilters={onlyCurrentEmployeesQuickFilter}
                  handleChangeQuickFilters={setOnlyCurrentEmployeesQuickFilter}
                />
              ),
            },
            searchingDate: {
              year: year,
              month: month,
              yearMonthQuickFilter: (
                <FilterByYearAndMonth
                  filterColor={"filter"}
                  year={year}
                  month={month}
                  handleChangeYearAndMonth={handleChangeYearAndMonth}
                  withMaxDate={false}
                />
              ),
              yearQuickFilter: (
                <FilterByYear
                  year={year}
                  handleChangeYear={handleChangeYear}
                  filterColor={"filter"}
                  withMaxDate={false}
                />
              ),
            },
          }}
        >
          <LoadingOverlay loading={legalEntityQFIsLoading}>
            {!legalEntityQFIsLoading && (
              <MultipleTabs
                panels={panels}
                selectedTab={selectedTab}
              />
            )}
          </LoadingOverlay>
        </MyTnAContext.Provider>
      ) : (
        <NotAuthorizedPage />
      )}
    </>
  );
};

export default MyTeamPresenceAndLeaves;
